// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { createPortal } from 'react-dom';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';
import type { Toast } from '@/lib/useToast';
import type { ReactElement } from 'react';

interface ToastContainerProps {
  readonly toasts: readonly Toast[];
  readonly onDismiss: (id: string) => void;
}

const config: Record<string, { Icon: typeof Info; bg: string; border: string; text: string; iconColor: string; bar: string }> = {
  success: { Icon: CheckCircle2, bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', iconColor: 'text-emerald-500', bar: 'bg-emerald-500' },
  error: { Icon: XCircle, bg: 'bg-destructive/10', border: 'border-destructive/30', text: 'text-destructive', iconColor: 'text-destructive', bar: 'bg-destructive' },
  info: { Icon: Info, bg: 'bg-primary-50', border: 'border-primary-500/30', text: 'text-primary-700', iconColor: 'text-primary-500', bar: 'bg-primary-500' },
  warning: { Icon: AlertTriangle, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', iconColor: 'text-amber-500', bar: 'bg-amber-500' },
};

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps): ReactElement | null {
  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none" style={{ maxWidth: 400 }}>
      {toasts.map((toast, idx) => {
        const c = config[toast.type] ?? config['info']!;
        const Icon = c!.Icon;
        // duration 0 = kullanıcı "Tamam" demeden kapanmaz; geri sayım çubuğu da çizilmez.
        const kalici = (toast.duration ?? 3500) === 0;
        return (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg ${c.bg} ${c.border} animate-slide-in-right relative overflow-hidden`}
            style={{ animationDelay: `${idx * 50}ms` }}
          >
            <Icon className={`w-5 h-5 ${c.iconColor} flex-shrink-0`} />
            <span className={`text-[14px] font-medium flex-1 ${c.text}`}>{toast.message}</span>
            {kalici ? (
              <button
                type="button"
                onClick={() => onDismiss(toast.id)}
                className={`px-3 py-1 rounded-lg text-[13px] font-semibold border ${c.border} ${c.text} hover:bg-foreground/5 transition-colors flex-shrink-0`}
              >
                Tamam
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onDismiss(toast.id)}
                className={`p-0.5 rounded-lg hover:bg-black/5 ${c.text} opacity-50 hover:opacity-100 transition-opacity`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {!kalici && (
              <div
                className={`absolute bottom-0 left-0 h-0.5 rounded-b-xl ${c.bar} animate-shrink-width`}
                style={{ animationDuration: `${toast.duration ?? 3500}ms` }}
              />
            )}
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
